import {IPerRoadLayerImperial, IPerRoadSeasonImperial, PerRoadLayerType} from "@/modules/perRoad/interfaces/perRoad";
import {UnitSystem} from "@/interfaces/units";
import {
  acModulusByGrade,
  acModulusDistribution,
  acThicknessDistribution,
  granularModulusDistribution,
  modulusByLayerType_psi,
  soilModulusDistribution,
  soilThicknessDistribution
} from "@/modules/perRoad/lib/layers/layerTypeDefaults";
import {
  createAcHorizontalDistributionPerformanceCriteria,
  createHorizontalMeDesignCriteria,
  createVerticalMeDesignCriteria
} from "@/modules/perRoad/lib/layers/performanceCriteria";
import {poissonRatios} from "@/modules/perRoad/lib/poissonRatio";
import {fall, spring, summer, winter} from "@/modules/perRoad/lib/layers/seasons";
import {temperatureAdjustedStiffnessPsi} from "@/modules/perRoad/lib/calculations/seasonalAdjustment";
import {DistributionTypes} from "@/lib/monteCarlo/interfaces";


/**
 * Create a default soil layer
 */

export const getBasicLayerSoil = (): IPerRoadLayerImperial => {
  return {
    id: -1,
    name: 'Default Soil',
    enabled: true,
    unitSystem: UnitSystem.Imperial,

    type: PerRoadLayerType.SOIL,
    pgGradeHigh: null,
    pgGradeLow: null,
    poissonRatio: 0.45,

    // Shallow copy but should be ok??
    modulus_psi: [{...soilModulusDistribution}],
    thickness_in: {...soilThicknessDistribution},

    performanceCriteria: [],

  }
};

const basicLayerAc: IPerRoadLayerImperial = {
  id: -1,
  name: 'Default AC',
  type: PerRoadLayerType.AC,
  unitSystem: UnitSystem.Imperial,
  enabled: true,
  poissonRatio: 0.35,
  pgGradeHigh: 70,
  pgGradeLow: -22,
  modulus_psi: [],
  thickness_in: acThicknessDistribution,
  performanceCriteria: [],
};

/**
 * Generate distributions for each layer for each season
 */

export const setAcLayerModulusBySeason = (layer: IPerRoadLayerImperial,
                                          seasons: IPerRoadSeasonImperial[] = [summer, fall, winter, spring]) => {
  const updatedLayer = layer;
  updatedLayer.modulus_psi = [];

  seasons.forEach((season) => {
    const newDist = {...acModulusDistribution};

    newDist.mean = acModulusByGrade[layer.pgGradeHigh || 70];

    newDist.mean = temperatureAdjustedStiffnessPsi(
      updatedLayer.pgGradeHigh!,
      season.temp_degF,
      updatedLayer.thickness_in.mean || 0,
    );
    // console.log(season.name, season.temp_degF, newDist.mean);
    newDist.location = Math.log(newDist.mean || 12);
    newDist.shape = 0.3;
    newDist.name = `${season.name}`;
    updatedLayer.modulus_psi.push(newDist);
  });

  return updatedLayer;
};


export const setOtherLayerElasticModulusBySeason = (layer: IPerRoadLayerImperial,
                                                    mean_modulus_psi: number = 20000,
                                                    seasons: IPerRoadSeasonImperial[] = [summer, fall, winter, spring]) => {
  const updatedLayer = layer;
  updatedLayer.modulus_psi = [];

  seasons.forEach((season) => {
    const newDist = {...granularModulusDistribution};

    newDist.mean = mean_modulus_psi;
    newDist.max = mean_modulus_psi * 10;
    newDist.min = 0;

    newDist.prob = DistributionTypes.lognormal;

    // console.log(season.name, season.temp_degF, newDist.mean);
    newDist.location = Math.log(newDist.mean);
    newDist.shape = 0.3;
    newDist.name = `${season.name}`;
    updatedLayer.modulus_psi.push(newDist);
  });

  return updatedLayer;
};

export const createNewAcLayer = function createNewAcLayer(layerType: PerRoadLayerType = PerRoadLayerType.AC,
                                                          seasons: IPerRoadSeasonImperial[] = [summer, fall, winter, spring],
                                                          thicknessDistribution = acThicknessDistribution,
                                                          performanceCriteria = [
                                                            createAcHorizontalDistributionPerformanceCriteria(),
                                                            createHorizontalMeDesignCriteria(),
                                                            createVerticalMeDesignCriteria(),
                                                          ]
) {
  // Deep copy
  const newLayer = JSON.parse(JSON.stringify(basicLayerAc));
  newLayer.name = `${newLayer.name}`;

  newLayer.thickness_in = thicknessDistribution;
  newLayer.performanceCriteria = performanceCriteria;

  return setAcLayerModulusBySeason(newLayer, seasons);
};

export const createNewGranularBase = (layerType: PerRoadLayerType = PerRoadLayerType.GRANULAR_BASE,
                                      seasons: IPerRoadSeasonImperial[] = []) => {

  // This has layertype and season parameters so as to have the same function signature as the other new layer functions
  const newLayer: IPerRoadLayerImperial = JSON.parse(JSON.stringify(basicLayerAc));
  newLayer.name = 'Default Granular Base';
  newLayer.type = PerRoadLayerType.GRANULAR_BASE;
  newLayer.thickness_in = {...acThicknessDistribution};

  newLayer.performanceCriteria = [
    createVerticalMeDesignCriteria(),
  ];

  const layerWithSeasons = setOtherLayerElasticModulusBySeason(newLayer, 20000);
  return layerWithSeasons;
};


export const createNewGenericLayer = (layerType: PerRoadLayerType,
                                      seasons: IPerRoadSeasonImperial[] = [],
                                      thicknessDistribution = acThicknessDistribution,
                                      modulusDistribution = granularModulusDistribution
): IPerRoadLayerImperial => {
  // This has season parameters so as to have the same function signature as the other new layer functions
  const newLayer: IPerRoadLayerImperial = JSON.parse(JSON.stringify(basicLayerAc));

  const mean_modulus_psi = modulusByLayerType_psi[layerType];

  newLayer.name = `Default ${layerType}`;
  newLayer.type = layerType;
  newLayer.thickness_in = {...thicknessDistribution};

  newLayer.modulus_psi = [];
  seasons.forEach((season) => {
    const newDist = {...modulusDistribution};
    newDist.location = Math.log(newDist.mean || 12);
    newDist.shape = 0.3;
    newDist.name = `${season.name}`;
    newLayer.modulus_psi.push(newDist);
  });

  newLayer.performanceCriteria = [];
  newLayer.poissonRatio = poissonRatios[layerType].mean;

  const layerWithSeasons = setOtherLayerElasticModulusBySeason(newLayer, mean_modulus_psi);

  return layerWithSeasons;
};


export const newDisabledSoilLayer = (): IPerRoadLayerImperial => {
  // const layer: IPerRoadLayerImperial = JSON.parse(JSON.stringify(basicLayerSoil));
  const layer = createNewGenericLayer(PerRoadLayerType.SOIL, [summer, fall, winter, spring]);
  layer.enabled = false;
  return layer;
};


export const newLayerFunctionsByType = {
  // Custom factories
  [PerRoadLayerType.AC]: createNewAcLayer,
  [PerRoadLayerType.CRACKED_AC]: createNewAcLayer,
  [PerRoadLayerType.GRANULAR_BASE]: createNewGranularBase,

  // Generic factory
  [PerRoadLayerType.ROCK]: createNewGenericLayer,
  [PerRoadLayerType.SOIL]: createNewGenericLayer,
  [PerRoadLayerType.BS_PCC]: createNewGenericLayer,
  [PerRoadLayerType.CS_PCC]: createNewGenericLayer,
  [PerRoadLayerType.RUBBLIZED_PCC]: createNewGenericLayer,
  [PerRoadLayerType.PCC]: createNewGenericLayer,
  [PerRoadLayerType.OTHER]: createNewGenericLayer,
};
